/** Heroes: experience, levels, the daily pools, and army legality.
 *
 * Every number a hero contributes to is built by `resolveStat` from modifiers
 * collected here (D9). The only place a skill is named in code is
 * `CODED_SKILLS`: a handful of secondary skills change a *rule* rather than a
 * *number* (Pathfinding rewrites step costing, Navigation switches the movement
 * pool, Diplomacy changes an offer), and a `Modifier` cannot express that.
 */

import type {
  ArmyId, ArtifactDefId, CreatureDefId, HeroId, ModifierSrcId, SkillDefId, SpellDefId,
  SubskillDefId,
} from "../ids.js";
import { asArtifactDefId, asModifierSrcId, asSkillDefId } from "./ids.js";
import type { Reason } from "../commands.js";
import type {
  ContentRegistry, CreatureDef, SubskillChoices, SubskillDef,
} from "../content/registry.js";
import { SUBSKILL_RUNGS, subskillById } from "../content/registry.js";
import type { Explained, Modifier } from "../modifiers.js";
import { resolveStat } from "../modifiers.js";
import { BP_ONE, clampInt, roundHalfAway } from "../numeric.js";
import type {
  ArmySlot, ArmyState, HeroState, PrimarySkill, PrimarySkills, SkillLevel, TerrainKind,
} from "../state.js";
import { BASE_SCOUTING_RADIUS } from "../map/fog.js";
import {
  IMPASSABLE, NEIGHBOUR_DX, NEIGHBOUR_DY, indexOf, isWaterCell, moveRules, riverKindMask,
  terrainAt, type MoveMode, type MoveRules,
} from "../map/create.js";
import { cellIndex, inBounds } from "../state.js";
import { entityKeys } from "../util/iter.js";
import type { ResumeToken } from "../state.js";
import type { Ctx, Mut, TokenData } from "./ctx.js";
import { optionResponses, patchHero, raise, rollWeighted, tokenPath } from "./ctx.js";
import { effectiveSkillLevel, specialtyModifiers } from "./specialty.js";
import { kingdomModifiers } from "./buildings.js";
import { freeSlotFor, withSlots } from "./slots.js";
import { payNum, payloadOf } from "./payload.js";

// ------------------------------------------------------------- coded skills

/**
 * Skills the kernel reads by id because their effect is a rule, not a number.
 * Everything else about these skills (their stat bonuses) still comes from the
 * content modifiers; only the switch they throw is coded.
 */
export const CODED_SKILLS = Object.freeze({
  pathfinding: asSkillDefId("pathfinding"),
  logistics: asSkillDefId("logistics"),
  navigation: asSkillDefId("navigation"),
  scouting: asSkillDefId("scouting"),
  diplomacy: asSkillDefId("diplomacy"),
  wisdom: asSkillDefId("wisdom"),
  learning: asSkillDefId("learning"),
  estates: asSkillDefId("estates"),
  mysticism: asSkillDefId("mysticism"),
  intelligence: asSkillDefId("intelligence"),
  necromancy: asSkillDefId("necromancy"),
});

export const MAX_SECONDARY_SKILLS = 8;
export const MAX_HEROES_PER_PLAYER = 8;

/** Level-up offers two secondary skills, as in the classic. */
export const SKILL_OFFERS = 2;

export function skillLevel(h: HeroState, def: SkillDefId): 0 | 1 | 2 | 3 {
  for (const s of h.secondary) if (s.def === def) return s.level;
  return 0;
}

/**
 * The level a *coded* rule should read: the skill the hero holds, plus the step
 * a specialty in that skill is worth. Every rule that branches on a skill level
 * asks this; `skillLevel` remains the raw holding, which is what the hero
 * screen shows and what the level-up offer works from.
 */
export function skillLevelOf(c: Ctx, h: HeroState, def: SkillDefId): 0 | 1 | 2 | 3 {
  return effectiveSkillLevel(c.content, c.content.heroes.get(h.def), h, def);
}

// ------------------------------------------------------------- modifiers

/**
 * Every modifier acting on a hero, in a fixed order: secondary skills, equipped
 * artifacts, the hero's specialty, then whatever the adventure map has stuck to
 * them. `resolveStat` sorts by source anyway (I3); the order here only decides
 * what a tooltip lists first.
 */
export function heroModifiers(c: Ctx, h: HeroState): readonly Modifier[] {
  const out: Modifier[] = [];
  for (const s of h.secondary) {
    const def = c.content.skills.get(s.def);
    if (def === undefined) continue;
    for (const m of def.levels[s.level - 1] ?? []) out.push(m);
    // The branch the player chose rides with the rung that opened it.
    for (const id of s.picks ?? []) {
      const sub = subskillById(def, id);
      if (sub === undefined) continue;
      for (const m of sub.modifiers) out.push(m);
    }
  }
  for (const slot of equippedSlots(h)) {
    const id = h.artifacts[slot];
    if (id === null || id === undefined) continue;
    const def = c.content.artifacts.get(id);
    if (def === undefined) continue;
    for (const m of def.modifiers) out.push(m);
  }
  for (const m of specialtyModifiers(c.content, c.content.heroes.get(h.def), h)) out.push(m);
  // Section 12: a Lighthouse works for every hero the kingdom has, not only for
  // the one standing under it.
  for (const m of kingdomModifiers(c, h.owner)) out.push(m);
  for (const m of h.modifiers) out.push(m);
  return out;
}

/** Equipped artifact slots in sorted order, so iteration is stable (I3). */
export function equippedSlots(h: HeroState): readonly string[] {
  return Object.keys(h.artifacts).sort();
}

export function statFor(
  c: Ctx, h: HeroState, stat: Modifier["stat"], base: number,
): Explained<number> {
  const mods = heroModifiers(c, h).filter((m) => m.stat === stat);
  return resolveStat(base, mods);
}

/**
 * The four primaries a sheet must print: the stored base plus every modifier
 * that names them (artifacts, specialty, kingdom, the rest of `heroModifiers`).
 *
 * `HeroState.primary` is the hero's own numbers - what a level-up grew, what a
 * scholar taught. A Wyrmfang Crown does not write those; it is a
 * modifier, and reading the stored field is how the crown looked like it did
 * nothing on the sheet while still applying in combat (combat already goes
 * through `statFor`).
 */
export function primaryFor(c: Ctx, h: HeroState): PrimarySkills {
  return {
    attack: statFor(c, h, "attack", h.primary.attack).value,
    defense: statFor(c, h, "defense", h.primary.defense).value,
    spellPower: statFor(c, h, "spellPower", h.primary.spellPower).value,
    knowledge: statFor(c, h, "knowledge", h.primary.knowledge).value,
  };
}

// ------------------------------------------------------------- movement

/**
 * The classic's base movement table, indexed by the speed of the slowest stack in the
 * army at the start of the day. An empty army moves as if the slowest stack
 * were fast, because there is nothing slowing the hero down.
 *
 * The shape is a hundred points per speed point from 4 upward, floored at 1500
 * and capped at 2300. It matters that the steps are single points rather than
 * three wide bands: the difference between a speed-6 and a speed-7 army is the
 * whole reason a player leaves the slow stack in the garrison, and a table that
 * pays the same for both makes that decision free.
 */
export const MOVEMENT_BY_SPEED: readonly number[] = Object.freeze([
  1500, 1500, 1500, 1500, 1600, 1700, 1800, 1900, 2000, 2100, 2200, 2300,
]);
export const MOVEMENT_MAX = 2300;
export const BASE_SEA_MOVEMENT = 1500;

export function slowestSpeed(c: Ctx, a: ArmyState | null): number {
  if (a === null) return MOVEMENT_BY_SPEED.length - 1;
  let slowest = -1;
  for (const s of a.slots) {
    if (s === null || s.count <= 0) continue;
    const def = c.content.creatures.get(s.def);
    if (def === undefined) continue;
    if (slowest < 0 || def.speed < slowest) slowest = def.speed;
  }
  return slowest < 0 ? MOVEMENT_BY_SPEED.length - 1 : slowest;
}

const SRC_ARMY_SPEED = asModifierSrcId("army.slowest");

/**
 * The day's land movement pool. The classic fixes it at the start of the day from the
 * army the hero had then; swapping in a faster stack later that day does not
 * buy back movement, which is why this is only ever called at the day boundary.
 */
export function explainMovement(c: Ctx, h: HeroState, sea: boolean): Explained<number> {
  const a = c.s.armies[h.army] ?? null;
  const speed = clampInt(slowestSpeed(c, a), 0, MOVEMENT_BY_SPEED.length - 1);
  const base = sea ? BASE_SEA_MOVEMENT : MOVEMENT_BY_SPEED[speed]!;
  const stat = sea ? "movementSea" : "movement";
  const mods = heroModifiers(c, h).filter((m) => m.stat === stat);
  const withBase = [...mods, {
    source: SRC_ARMY_SPEED, stat, op: "add" as const, value: 0,
  } satisfies Modifier];
  return resolveStat(base, withBase);
}

export const dailyMovement = (c: Ctx, h: HeroState): number =>
  explainMovement(c, h, h.boat !== null).value;

// ------------------------------------------------------------------ mana

export const MANA_PER_KNOWLEDGE = 10;
/** A hero regains one point a day before Meditation (Section 5, the expansion table). */
export const BASE_MANA_REGEN = 1;

export function maxMana(c: Ctx, h: HeroState): number {
  const knowledge = statFor(c, h, "knowledge", h.primary.knowledge).value;
  const base = (knowledge < 0 ? 0 : knowledge) * MANA_PER_KNOWLEDGE;
  const v = statFor(c, h, "mana", base).value;
  return v < 0 ? 0 : v;
}

/**
 * Keep current mana in step with a change to the cap.
 *
 * Equipping a Wyrmfang Crown raises Knowledge and therefore the pool;
 * The classic adds the same delta to the hero's current points, and taking the crown
 * off subtracts it, never below zero and never above the new cap. Called after
 * any command that can change what `maxMana` would return.
 */
export function retuneMana(c: Ctx, heroId: HeroId, prevMax: number): void {
  const h = c.s.heroes[heroId];
  if (h === undefined) return;
  const next = maxMana(c, h);
  const delta = next - prevMax;
  if (delta === 0) return;
  let mana = h.mana + delta;
  if (mana < 0) mana = 0;
  if (mana > next) mana = next;
  if (mana === h.mana) return;
  patchHero(c, heroId, { mana });
}

export function manaRegen(c: Ctx, h: HeroState): number {
  const v = statFor(c, h, "manaRegen", BASE_MANA_REGEN).value;
  return v < 0 ? 0 : v;
}

// -------------------------------------------------------------- scouting

export function scoutingRadiusOf(c: Ctx, h: HeroState): number {
  const v = statFor(c, h, "scouting", BASE_SCOUTING_RADIUS).value;
  return v < 1 ? 1 : v;
}

// ------------------------------------------------------------- move rules

/** Native terrain is the faction's, and only while the whole army shares it. */
export function nativeTerrainOf(c: Ctx, h: HeroState): TerrainKind | null {
  const f = c.content.factions.get(h.faction);
  if (f === undefined) return null;
  if (!armyIsFactionPure(c, h)) return null;
  return f.nativeTerrain;
}

/**
 * The four modifier sources adventure magic hangs on a hero. They live here
 * rather than in `spells.ts` because the rules that *read* them are here: the
 * pathfinder's mode, the day's crossing tally and the scrying mask are all hero
 * questions, and putting the ids beside the spells would make `hero.ts` import
 * the module that imports it.
 */
export const SRC_FLY = asModifierSrcId("spell.skyward");
export const SRC_WATER_WALK = asModifierSrcId("spell.wave_stride");
export const SRC_DIMENSION_DOOR = asModifierSrcId("spell.blink_step");
/**
 * The once-a-day spells, each marking the hero with a turn-scoped modifier.
 *
 * The same trick Blink Step's tally uses: a `movement` modifier of zero
 * expires at the day boundary on its own, so "has this hero cast it today"
 * needs no field on `HeroState` and no clean-up anywhere.
 */
export const SRC_SPELL_HARVEST = asModifierSrcId("spell.reaping");
export const SRC_TRANSMUTE = asModifierSrcId("spell.transmute");
export const SRC_METEOR_STRIKE = asModifierSrcId("spell.meteor_strike");
export const SRC_BLOOD_RITUAL = asModifierSrcId("spell.blood_rite");
/** Indexed by mastery 0..3; which one is present is how Disguise carries it. */
export const SRC_DISGUISE_BY_MASTERY: readonly ModifierSrcId[] = Object.freeze([
  asModifierSrcId("spell.masquerade.0"),
  asModifierSrcId("spell.masquerade.1"),
  asModifierSrcId("spell.masquerade.2"),
  asModifierSrcId("spell.masquerade.3"),
]);

export function moveModeOf(c: Ctx, h: HeroState): MoveMode {
  if (h.boat !== null) {
    // The expansion's Airship is a boat that also crosses land, so being aboard one is
    // `fly` rather than `sea`. Read off the object's payload rather than its
    // id: "which boats fly" is content's to say, and a rule that named
    // `boat_airship` would be exactly the kind of hard-coded def check
    // AGENTS.md forbids. `payloadOf` merges the def's payload under the
    // instance's, so the flag rides on the definition and every airship on the
    // map inherits it.
    const boat = c.s.objects[h.boat];
    if (boat !== undefined && payNum(payloadOf(c, boat), "flies", 0) > 0) return "fly";
    return "sea";
  }
  for (const m of h.modifiers) {
    if (m.source === SRC_FLY) return "fly";
    if (m.source === SRC_WATER_WALK) return "waterWalk";
  }
  // Radiant Wings and Cloudstep Boots are the same rule as the spell, worn
  // instead of cast, so they are asked for as a stat rather than by source.
  if (statFor(c, h, "moveFly", 0).value > 0) return "fly";
  if (statFor(c, h, "moveWaterWalk", 0).value > 0) return "waterWalk";
  return "land";
}

/**
 * How much of a hero's level another player is allowed to read (Section 30,
 * Disguise). At no mastery the truth shows; each step hides more of it, and the
 * reading never drops below level 1 because "there is a hero there" is not the
 * part the spell hides.
 */
export const DISGUISE_SHOWN_BP: readonly number[] = Object.freeze([7500, 5000, 3300, 2500]);

export function disguisedLevel(h: HeroState): number {
  // No Disguise on the hero means the whole truth; the table is what the spell
  // leaves showing, so its first entry is a mastery, not a default.
  let bp = BP_ONE;
  for (const m of h.modifiers) {
    const i = SRC_DISGUISE_BY_MASTERY.indexOf(m.source);
    if (i >= 0) bp = DISGUISE_SHOWN_BP[i]!;
  }
  if (bp >= BP_ONE) return h.level;
  const shown = roundHalfAway((h.level * bp) / BP_ONE);
  return shown < 1 ? 1 : shown;
}

/** Pathfinding: the skill the hero learned, or the boots he is wearing. */
export function pathfindingLevel(c: Ctx, h: HeroState): 0 | 1 | 2 | 3 {
  const learned = skillLevelOf(c, h, CODED_SKILLS.pathfinding);
  const worn = clampInt(statFor(c, h, "pathfinding", 0).value, 0, 3);
  return (worn > learned ? worn : learned) as 0 | 1 | 2 | 3;
}

export function moveRulesFor(c: Ctx, h: HeroState): MoveRules {
  const f = c.content.factions.get(h.faction);
  const pure = armyIsFactionPure(c, h);
  const perk = pure ? f : undefined;
  return moveRules({
    mode: moveModeOf(c, h),
    pathfinding: pathfindingLevel(c, h),
    nativeTerrain: nativeTerrainOf(c, h),
    budget: h.movementLeft,
    lastStepFree: true,
    amphibious: perk?.amphibious === true,
    riverAsRoad: (perk?.riverAsRoad === true)
      || statFor(c, h, "riverAsRoad", 0).value > 0,
    riverRoadMask: riverKindMask(perk?.riverRoadKinds),
    riverRoadCost: perk?.riverRoadCost ?? 75,
    roadCostCap: perk?.roadCostCap ?? 0,
    extraNative: perk?.extraNative ?? [],
    terrainCosts: perk?.terrainCosts ?? {},
    underworldNative: perk?.underworldNative === true,
    shoreWalk: perk?.shoreWalk === true,
    extraCells: boatCells(c, h),
  });
}

/**
 * The one step that changes a hero's mode.
 *
 * On foot: every water cell a hull sits on, so a walk can end by boarding.
 * Aboard a sailing hull: the eight cells around him that are land, so a
 * voyage can end by landing. A flying hull never lands this way - it crosses
 * the shore like any other cell and the hero stays aboard. Nothing else is
 * opened: a boat cell is a dead end for the pathfinder in land mode, because
 * the water beyond it is still water, which is exactly the classic rule that
 * boarding ends the day.
 */
function boatCells(c: Ctx, h: HeroState): ReadonlySet<number> | null {
  const m = c.s.map;
  const out = new Set<number>();
  if (h.boat !== null) {
    if (moveModeOf(c, h) !== "sea") return null;
    for (let k = 0; k < 8; k++) {
      const nx = h.x + NEIGHBOUR_DX[k]!, ny = h.y + NEIGHBOUR_DY[k]!;
      if (!inBounds(m, nx, ny, h.z)) continue;
      const i = cellIndex(m, nx, ny, h.z);
      if (!isWaterCell(m, i) && m.moveCost[i] !== IMPASSABLE) out.add(i);
    }
    return out.size > 0 ? out : null;
  }
  for (const id of entityKeys(c.s.objects)) {
    const o = c.s.objects[id]!;
    if (o.kind !== "boat" || o.removed) continue;
    // An empty footprint is the object's own cell, as `layFootprint` reads it.
    const declared = c.content.mapObjects.get(o.def)?.footprint ?? [];
    const footprint: readonly (readonly [number, number])[] = declared.length === 0 ? [[0, 0]] : declared;
    for (const [dx, dy] of footprint) {
      if (!inBounds(m, o.x + dx, o.y + dy, o.z)) continue;
      const i = cellIndex(m, o.x + dx, o.y + dy, o.z);
      if (isWaterCell(m, i)) out.add(i);
    }
  }
  return out.size > 0 ? out : null;
}

/** True when every living stack in the army is the hero's own faction (or the army is empty). */
export function armyIsFactionPure(c: Ctx, h: HeroState): boolean {
  const a = c.s.armies[h.army];
  if (a === undefined) return true;
  for (const s of a.slots) {
    if (s === null || s.count <= 0) continue;
    const def = c.content.creatures.get(s.def);
    if (def === undefined) continue;
    if (def.faction !== h.faction) return false;
  }
  return true;
}

export const heroCellIndex = (c: Ctx, h: HeroState): number =>
  indexOf(c.s.map, h.x, h.y, h.z);

export const terrainUnderHero = (c: Ctx, h: HeroState): TerrainKind =>
  terrainAt(c.s.map, heroCellIndex(c, h));

// ------------------------------------------------------------- experience

export function levelForExperience(content: ContentRegistry, xp: number): number {
  const t = content.levels.thresholds;
  let level = 1;
  for (let i = 1; i < t.length; i++) {
    if (xp >= t[i]!) level = i + 1; else break;
  }
  return level;
}

export const experienceForLevel = (content: ContentRegistry, level: number): number => {
  const t = content.levels.thresholds;
  if (level <= 1) return 0;
  return t[level - 1] ?? t[t.length - 1] ?? 0;
};

const PRIMARY_ORDER: readonly PrimarySkill[] =
  Object.freeze(["attack", "defense", "spellPower", "knowledge"] as const);

/** Class tables gain a "+" row from level 10, as in the classic. */
export function primaryChanceRow(
  content: ContentRegistry, heroClass: string, level: number,
): readonly [number, number, number, number] {
  const table = content.levels.primaryChance;
  const late = table[`${heroClass}+`];
  if (level >= 10 && late !== undefined) return late as [number, number, number, number];
  const early = table[heroClass];
  if (early !== undefined) return early as [number, number, number, number];
  // An unknown class is a content gap, not a player action: roll evenly rather
  // than throwing in the middle of a command.
  return [2500, 2500, 2500, 2500];
}

export function rollPrimary(c: Ctx, h: HeroState, level: number): PrimarySkill {
  const hd = c.content.heroes.get(h.def);
  const row = primaryChanceRow(c.content, hd?.heroClass ?? "", level);
  return PRIMARY_ORDER[rollWeighted(c, "hero.levelup", row)] ?? "attack";
}

/**
 * Relative chance a class is offered `skill`. No class table (or no table at
 * all) is a uniform 1, which is how testdata keeps the old even draw. A listed
 * class treats a missing / zero skill as banned for a *new* offer; a banned
 * skill the hero already holds is offered as an upgrade at weight 1, which is
 * The classic's "you learned it at a Hedge Hut, you may still raise it" rule.
 */
export function skillWeight(
  content: ContentRegistry, heroClass: string, skill: SkillDefId, held: boolean,
): number {
  const table = content.levels.secondaryChance;
  if (table === undefined) return 1;
  const row = table[heroClass];
  if (row === undefined) return 1;
  const w = row[skill as string];
  if (w === undefined || w <= 0) return held ? 1 : 0;
  return w;
}

/**
 * The secondary skills a hero may be offered. The classic offers one upgrade of a
 * skill already held and one new skill where both exist; with eight skills held
 * only upgrades are possible, and with everything at expert there is no offer
 * and the level-up is silent.
 *
 * New skills (and upgrades) are drawn by `secondaryChance` when the class has
 * a row, so a Bonecaller sees Gravecalling far more often than Archery, and
 * never Leadership. With no row the draw is uniform, matching the tests that
 * never authored a table.
 */
export function skillOffers(c: Ctx, h: HeroState): readonly SkillDefId[] {
  const hd = c.content.heroes.get(h.def);
  const heroClass = hd?.heroClass ?? "";
  const held = new Set<string>(h.secondary.map((s) => s.def as string));

  const upgrades: SkillDefId[] = [];
  for (const s of h.secondary) if (s.level < 3) upgrades.push(s.def);
  upgrades.sort();

  const fresh: SkillDefId[] = [];
  if (h.secondary.length < MAX_SECONDARY_SKILLS) {
    const ids = [...c.content.skills.keys()].sort();
    for (const id of ids) {
      if (held.has(id as string)) continue;
      if (skillWeight(c.content, heroClass, id, false) <= 0) continue;
      fresh.push(id);
    }
  }

  const out: SkillDefId[] = [];
  const takeFrom = (pool: SkillDefId[]): void => {
    if (pool.length === 0) return;
    const weights = pool.map((id) => skillWeight(c.content, heroClass, id, held.has(id as string)));
    const i = rollWeighted(c, "hero.levelup", weights);
    out.push(pool[i]!);
    pool.splice(i, 1);
  };
  takeFrom(upgrades);
  takeFrom(fresh);
  while (out.length < SKILL_OFFERS && (upgrades.length > 0 || fresh.length > 0)) {
    takeFrom(upgrades.length > 0 ? upgrades : fresh);
  }
  return forceWisdomOffer(c, h, out);
}

/**
 * The classic's Wisdom guarantee: a hero who is not yet an expert must be *offered*
 * Wisdom somewhere inside each three-level window (2-3, 5-6, 8-9, ...), or a
 * magic hero can roll past level 12 unable to cast anything above level two.
 *
 * Implemented at the close of each window rather than by remembering whether
 * the window's earlier level already offered it: at the window's first level
 * Wisdom can still come up on its own roll, and at its last it is substituted
 * into the second slot. That needs no new `HeroState` field and, because the
 * substitution happens after both rolls, it does not disturb the RNG stream.
 */
function forceWisdomOffer(
  c: Ctx, h: HeroState, offers: readonly SkillDefId[],
): readonly SkillDefId[] {
  const wisdom = CODED_SKILLS.wisdom;
  if (h.level % 3 !== 0) return offers;
  if (skillLevel(h, wisdom) >= 3) return offers;
  if (offers.includes(wisdom)) return offers;
  // A forced offer the hero could not legally take is a dead prompt: with eight
  // skills already held, an unheld Wisdom is not learnable at all.
  if (skillLevel(h, wisdom) === 0 && h.secondary.length >= MAX_SECONDARY_SKILLS) return offers;
  if (c.content.skills.get(wisdom) === undefined) return offers;
  if (offers.length === 0) return [wisdom];
  return [offers[0]!, wisdom];
}

export function learnSkill(c: Ctx, h: HeroState, def: SkillDefId): HeroState {
  const cur = h.secondary;
  const at = cur.findIndex((s) => s.def === def);
  let next: HeroState["secondary"];
  if (at >= 0) {
    const lvl = cur[at]!.level;
    if (lvl >= 3) return h;
    // Spread rather than rebuild: a skill going advanced -> expert keeps the
    // subskill it already picked, and only the level moves.
    const raised = { ...cur[at]!, def, level: (lvl + 1) as SkillLevel };
    next = [...cur.slice(0, at), raised, ...cur.slice(at + 1)];
  } else {
    if (cur.length >= MAX_SECONDARY_SKILLS) return h;
    next = [...cur, { def, level: 1 as SkillLevel }];
  }
  patchHero(c, h.id, { secondary: next });
  return c.s.heroes[h.id]!;
}

// -------------------------------------------------------------- subskills

/**
 * The branch inside a secondary skill.
 *
 * Section 5's skills, branched the way a modern remake branches them - The expansion
 * reference this project is written against has no subskills, so nothing here
 * is a parity claim.
 *
 * A skill's rungs are the same for every hero; the subskill is the part the
 * player chooses. Reaching advanced opens one pick from `subskills.advanced`
 * and reaching expert one from `subskills.expert`, and the chosen modifiers
 * join the rung's own in `heroModifiers`.
 *
 * What is owed is *derived*, never stored: a rung is settled when `picks` names
 * one of its choices. That is what makes the pick survive every road into
 * `learnSkill` - a level-up, a witch hut, a university - without each of them
 * having to remember to open a prompt, and what makes a hero loaded from a save
 * written before subskills existed simply owe the picks their levels imply.
 */
export interface SubskillDebt {
  readonly skill: SkillDefId;
  readonly rung: keyof SubskillChoices;
  readonly choices: readonly SubskillDef[];
}

/**
 * The first pick this hero owes, in `secondary` order, or null.
 *
 * Order is the hero's own skill order rather than anything rolled, so two
 * states that differ only in when a skill was learned ask in the same order
 * (I3).
 */
export function pendingSubskill(
  content: ContentRegistry, h: HeroState,
): SubskillDebt | null {
  for (const s of h.secondary) {
    const def = content.skills.get(s.def);
    if (def === undefined) continue;
    const picks = s.picks ?? [];
    for (const rung of SUBSKILL_RUNGS) {
      if (s.level < (rung === "expert" ? 3 : 2)) continue;
      const choices = def.subskills[rung];
      if (choices.length === 0) continue;
      if (choices.some((sub) => picks.includes(sub.id))) continue;
      return { skill: s.def, rung, choices };
    }
  }
  return null;
}

/**
 * Take one subskill. Returns whether the hero changed.
 *
 * Refuses anything the hero is not owed - a subskill of a skill they do not
 * hold, of a rung they have not reached, or a second pick on a settled rung -
 * so the only way into `picks` is a debt that `pendingSubskill` agreed existed.
 */
export function pickSubskill(
  c: Ctx, h: HeroState, skill: SkillDefId, sub: SubskillDefId,
): boolean {
  const at = h.secondary.findIndex((s) => s.def === skill);
  if (at < 0) return false;
  const state = h.secondary[at]!;
  const def = c.content.skills.get(skill);
  if (def === undefined) return false;

  const picks = state.picks ?? [];
  for (const rung of SUBSKILL_RUNGS) {
    const choices = def.subskills[rung];
    if (!choices.some((x) => x.id === sub)) continue;
    if (state.level < (rung === "expert" ? 3 : 2)) return false;
    if (choices.some((x) => picks.includes(x.id))) return false;
    const raised = { ...state, picks: [...picks, sub] };
    patchHero(c, h.id, {
      secondary: [...h.secondary.slice(0, at), raised, ...h.secondary.slice(at + 1)],
    });
    return true;
  }
  return false;
}

/**
 * Pay out every subskill pick a hero is owed, prompting for each.
 *
 * The same shape as `settleLevelUps`, and for the same reason: the picks are
 * settled one at a time because only one interaction may be open, and a pick
 * that cannot be prompted for is taken with the first choice rather than
 * dropped - a rung silently left unpicked would be a permanently weaker hero
 * with nothing on screen to explain it.
 */
export function settleSubskillPicks(c: Ctx, id: HeroId, carry?: ResumeToken): boolean {
  for (;;) {
    const h = c.s.heroes[id];
    if (h === undefined) return false;
    const owed = pendingSubskill(c.content, h);
    if (owed === null) return false;
    const choices = owed.choices;
    const take = (i: number): boolean => pickSubskill(c, h, owed.skill, choices[i]!.id);

    if (choices.length > 1) {
      const data: Mut<TokenData> = {
        hero: id, skill: owed.skill as string,
        choices: choices.map((x) => x.id as string).join(","),
        path: carry === undefined ? [] : [...tokenPath(carry)],
      };
      // Whether or not the question could be asked, this hero is done for now.
      //
      // `raise` refuses while another interaction is open, and taking a branch
      // on the player's behalf at that point is exactly wrong: `settleAllLevel-
      // Ups` runs after EVERY command, so the refusal usually means the prompt
      // for this very debt is on screen being read - and the pick would land
      // first, settle the rung, and turn the player's answer into a no-op.
      //
      // Nothing is lost by waiting, which is the whole return on deriving the
      // debt rather than storing it: the level and the picks still say a branch
      // is owed, and the next command asks again. A level-up cannot do this -
      // an unasked level is gone - which is why that loop takes the first offer
      // and this one does not.
      return raise(c, h.owner,
        {
          kind: "PICK_SUBSKILL", heroId: id, skill: owed.skill,
          choices: choices.map((x) => x.id),
        },
        optionResponses(choices.length),
        { kind: "SUBSKILL", data });
    }
    // A pool of one is not a question. `take` failing would spin this loop for
    // ever, so content that authored an unreachable choice loses the rung.
    if (!take(0)) return false;
  }
}

/** Raise the primary skill for one level and return the skill that grew. */
export function applyOneLevel(c: Ctx, id: HeroId): PrimarySkill {
  const h = c.s.heroes[id]!;
  const level = h.level + 1;
  const primary = rollPrimary(c, h, level);
  patchHero(c, id, {
    level,
    primary: { ...h.primary, [primary]: h.primary[primary] + 1 },
  });
  c.events.push({ type: "HeroLeveledUp", hero: id, level, primary });
  return primary;
}

/** Levels still owed by the hero's current experience. */
export function pendingLevels(c: Ctx, id: HeroId): number {
  const h = c.s.heroes[id];
  if (h === undefined) return 0;
  const target = levelForExperience(c.content, h.experience);
  return target > h.level ? target - h.level : 0;
}

/** Learning scales incoming experience; the expansion's 20/40/60 lives in the skill data. */
export function scaleExperience(c: Ctx, h: HeroState, amount: number): number {
  const mods = heroModifiers(c, h).filter((m) => m.stat === "experienceGain");
  if (mods.length === 0) return amount;
  const scaled = resolveStat(amount, mods).value;
  return scaled < 0 ? 0 : scaled;
}

/** Add experience and emit the event. Levels are applied by the caller. */
export function grantExperience(c: Ctx, id: HeroId, rawAmount: number): number {
  const h = c.s.heroes[id];
  if (h === undefined || rawAmount <= 0) return 0;
  const amount = scaleExperience(c, h, rawAmount);
  const total = h.experience + amount;
  patchHero(c, id, { experience: total });
  c.events.push({ type: "HeroGainedExperience", hero: id, amount, total });
  return amount;
}

// ---------------------------------------------------------------- army

export const ARMY_SLOTS = 7;

export const emptyArmySlots = (): (ArmySlot | null)[] =>
  [null, null, null, null, null, null, null];

export function armyCount(a: ArmyState): number {
  let n = 0;
  for (const s of a.slots) if (s !== null && s.count > 0) n++;
  return n;
}

export const armyEmpty = (a: ArmyState): boolean => armyCount(a) === 0;

export function setSlot(c: Ctx, id: ArmyId, index: number, slot: ArmySlot | null): void {
  const a = c.s.armies[id];
  if (a === undefined || index < 0 || index >= a.slots.length) return;
  const slots = [...a.slots];
  slots[index] = slot !== null && slot.count > 0 ? slot : null;
  (c.s.armies as Record<ArmyId, ArmyState>)[id] = { ...a, slots };
}

/**
 * Fold creatures into an army: top up a matching stack first, then take the
 * lowest free slot. Returns the number actually placed, which is 0 when the
 * army is full of other creatures.
 */
export function addCreatures(
  c: Ctx, id: ArmyId, def: CreatureDefId, count: number,
): number {
  const a = c.s.armies[id];
  if (a === undefined || count <= 0) return 0;
  for (let i = 0; i < a.slots.length; i++) {
    const s = a.slots[i];
    if (s !== null && s !== undefined && s.def === def) {
      setSlot(c, id, i, { def, count: s.count + count });
      return count;
    }
  }
  for (let i = 0; i < a.slots.length; i++) {
    const s = a.slots[i];
    if (s === null || s === undefined) {
      setSlot(c, id, i, { def, count });
      return count;
    }
  }
  return 0;
}

export function hasFreeSlotFor(a: ArmyState, def: CreatureDefId): boolean {
  for (const s of a.slots) {
    if (s === null) return true;
    if (s.def === def) return true;
  }
  return false;
}

export function armyValue(content: ContentRegistry, a: ArmyState | null): number {
  if (a === null) return 0;
  let v = 0;
  for (const s of a.slots) {
    if (s === null || s.count <= 0) continue;
    const def = content.creatures.get(s.def);
    if (def === undefined) continue;
    v += def.aiValue * s.count;
  }
  return v;
}

/** Gold price of an army, used by surrender and by joining offers. */
export function armyGoldCost(content: ContentRegistry, a: ArmyState | null): number {
  if (a === null) return 0;
  let v = 0;
  for (const s of a.slots) {
    if (s === null || s.count <= 0) continue;
    const def = content.creatures.get(s.def);
    if (def === undefined) continue;
    v += (def.cost.gold ?? 0) * s.count;
  }
  return v;
}

export function creatureOf(c: Ctx, def: CreatureDefId): CreatureDef | null {
  return c.content.creatures.get(def) ?? null;
}

// --------------------------------------------------------------- artifacts

export function artifactSlotFree(h: HeroState, slot: string): boolean {
  const cur = h.artifacts[slot];
  return cur === null || cur === undefined;
}

/**
 * Hand a hero an artifact. Worn at once when a socket of its family is free -
 * The classic puts a found clover on the doll, and a pickup that only ever landed in
 * the bag was a luck bonus most players never noticed they had to switch on -
 * and into the bag otherwise, or when the caller says so (`wear: false`; the
 * Grail is carried, never worn). Wearing is the same state change as
 * `EQUIP_ARTIFACT`, mana retune included, and raises the same event.
 */
export function giveArtifact(c: Ctx, id: HeroId, art: ArtifactDefId, wear = true): void {
  const h = c.s.heroes[id];
  if (h === undefined) return;
  const def = c.content.artifacts.get(art);
  const slot = wear && def !== undefined ? freeSlotFor(h, def) : null;
  if (slot === null) {
    patchHero(c, id, { backpack: [...h.backpack, art] });
    c.events.push({ type: "ArtifactGained", hero: id, artifact: art });
    return;
  }
  const prevMax = maxMana(c, h);
  patchHero(c, id, { artifacts: withSlots(h, { [slot]: art }) });
  retuneMana(c, id, prevMax);
  c.events.push({ type: "ArtifactGained", hero: id, artifact: art });
  c.events.push({ type: "ArtifactEquipped", hero: id, artifact: art, slot });
}

/**
 * The spellbook is an equipment slot, not a flag: `spell_book` sits in it and
 * "your hero cannot cast a single spell without it" is the whole of its
 * content. An absent key means the slot was never decided and the hero carries
 * the book they were trained with; an explicit `null` means the book has been
 * taken away, and that is the state this rule bites on.
 */
export const SPELLBOOK_SLOT = "spellbook";
/** The artifact that fills that slot. Content ships exactly one. */
export const SPELLBOOK_ARTIFACT = asArtifactDefId("spell_book");

export const hasSpellbook = (h: HeroState): boolean =>
  h.artifacts[SPELLBOOK_SLOT] !== null;

/**
 * Wisdom caps the spell level a hero may learn *or* cast: without it, levels 1
 * and 2 only; basic, advanced and expert each raise the ceiling by one
 * (Section 29, and the skill's own description - "Your hero may learn spells of
 * level 3" at basic).
 */
export const WISDOM_SPELL_LEVEL: readonly number[] = Object.freeze([2, 3, 4, 5]);

export function maxSpellLevel(h: HeroState, content?: ContentRegistry): number {
  const lvl = content === undefined
    ? skillLevel(h, CODED_SKILLS.wisdom)
    : effectiveSkillLevel(content, content.heroes.get(h.def), h, CODED_SKILLS.wisdom);
  return WISDOM_SPELL_LEVEL[lvl] ?? 2;
}

/**
 * Why this hero may not learn this spell, or null.
 *
 * The one gate. A shrine, a scholar, a mage guild and a scenario grant all ask
 * it, so a hero cannot pick up a level-5 spell from an object that forgot to
 * check Wisdom - which is exactly the second learning path this exists to
 * prevent.
 */
export function learnIllegality(c: Ctx, h: HeroState, spell: SpellDefId): Reason | null {
  if (!hasSpellbook(h)) return { code: "NO_SPELLBOOK" };
  const def = c.content.spells.get(spell);
  if (def === undefined) return { code: "NO_SUCH_ENTITY", id: spell as string };
  if (def.level > maxSpellLevel(h, c.content)) return { code: "TARGET_INVALID" };
  if (h.spellbook.includes(spell)) return { code: "TARGET_INVALID" };
  return null;
}

/** Teach a spell if the gate allows it. Returns whether the book grew. */
export function learnSpell(c: Ctx, id: HeroId, spell: SpellDefId): boolean {
  const h = c.s.heroes[id];
  if (h === undefined) return false;
  if (learnIllegality(c, h, spell) !== null) return false;
  patchHero(c, id, { spellbook: [...h.spellbook, spell].sort() });
  c.events.push({ type: "SpellLearned", hero: id, spell });
  return true;
}

// ------------------------------------------------------------- level-ups

/**
 * Pay out every level a hero's experience has bought.
 *
 * The primary skill is rolled and applied immediately - there is no choice in
 * it - and the secondary-skill offer becomes the one prompt. Levels are settled
 * one at a time: the token remembers how many are still owed, so a hero who
 * gains three levels from one bank answers three times.
 *
 * When another prompt is already open the offer is taken with the first choice
 * rather than dropped, because a level must not be silently lost.
 */
export function settleLevelUps(c: Ctx, id: HeroId, carry?: ResumeToken): boolean {
  let owed = pendingLevels(c, id);
  while (owed > 0) {
    applyOneLevel(c, id);
    owed -= 1;
    const h = c.s.heroes[id]!;
    const offers = skillOffers(c, h);
    if (offers.length === 0) continue;
    if (offers.length === 1) { learnSkill(c, h, offers[0]!); continue; }
    const data: Mut<TokenData> = {
      hero: id, offers: offers.join(","), remaining: owed,
      path: carry === undefined ? [] : [...tokenPath(carry)],
    };
    const asked = raise(c, h.owner,
      { kind: "LEVEL_UP", heroId: id, choices: offers.map((o) => o as string) },
      optionResponses(offers.length),
      { kind: "LEVEL_UP", data });
    if (asked) return true;
    learnSkill(c, h, offers[0]!);
  }
  // Levels first, then the branches those levels opened: a skill that reached
  // advanced three levels ago and one that reached it just now are the same
  // debt by the time the last level is paid, and asking here means the offer
  // prompt and the branch prompt are never open at once.
  return settleSubskillPicks(c, id, carry);
}

/** Settle every hero that is owed a level, in id order. Returns true if a prompt opened. */
export function settleAllLevelUps(c: Ctx, carry?: ResumeToken): boolean {
  for (const id of entityKeys(c.s.heroes)) {
    if (settleLevelUps(c, id as HeroId, carry)) return true;
  }
  return false;
}
